import { translate } from '@/lib/i18n';

/**
 * Represents a user-friendly, localized error intended for UI display.
 *
 * @property {string} name - A localized, user-facing string that serves as the name of the error (e.g., "Network error").
 * @property {string} message - A localized, user-facing message that describes the error in detail (e.g., "An unexpected network error occurred.").
 *
 * @example
 *   throw new LocalizedError('Verbindingsfout', 'Er is een onverwachte netwerkfout opgetreden.');
 *
 *   // Usage
 *   try {
 *     // ...
 *   } catch (e) {
 *     if (e instanceof LocalizedError) {
 *       showError(e.name, e.message);
 *     }
 *   }
 */
class LocalizedError extends Error {
  constructor(localizedName: string, localizedMessage: string) {
    super(localizedMessage);
    this.name = localizedName;
  }
}

const getErrorKey: Record<ErrorCategory, (error: Error) => string> = {
  ApplePay: (_error) => 'default',
  CreditCard: (_error) => 'default',
};

export function fromPaymentsSDKError(
  category: ErrorCategory,
  error: Error
): LocalizedError {
  const errorKey = getErrorKey[category]?.(error) || 'default';
  const i18nKey = `PaymentServices.${category}.errors.${errorKey}`;
  const localizedName = translate(`${i18nKey}.name`) ?? 'Error';
  const localizedMessage =
    translate(`${i18nKey}.message`) ?? `${i18nKey}.message`;

  return new LocalizedError(localizedName, localizedMessage);
}

export type ErrorCategory = 'CreditCard' | 'ApplePay';

export default LocalizedError;
